import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Icon, Modal, useToast } from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canGameLock } from '@/app/staffAccess'
import { hierarchyLockUiBlocked } from './hierarchy'
import type { HierarchyNode } from './hierarchy'

export type GameLockKind =
  | 'sportsbook'
  | 'casino'
  | 'card'
  | 'liveGame'
  | 'premiumSportsbook'
  | 'horseRacing'
  | 'matchOdds'
  | 'bookmaker'
  | 'customSession'
  | 'completedMatch'
  | 'tiedMatch'
  | 'lineSession'
  | 'transfer'

type GameStatusGame =
  | 'sportsbook'
  | 'casino'
  | 'cardGame'
  | 'liveGame'
  | 'premiumSportsbook'
  | 'horseRacing'
  | 'matchOdds'
  | 'bookmaker'
  | 'customSession'
  | 'completedMatch'
  | 'tiedMatch'
  | 'lineSession'
  | 'cricket'
  | 'uofMarkets'
  | 'esports'
  | 'greyhoundRacing'
  | 'darts'
  | 'election'
  | 'tennis'
  | 'soccer'
  | 'soccerSuper'
  | 'sportHorseRacing'
  | 'volleyball'
  | 'snooker'
  | 'rugbyLeague'
  | 'artemis'
  | 'rugbyUnion'
  | 'googleAuthenticator'

type ProductRow = {
  label: string
  kind?: GameLockKind
  game?: GameStatusGame
  hidePadlockWhenGlobal?: boolean
  blockedBySportsbook?: boolean
}

const PRODUCTS: ProductRow[] = [
  { label: 'Sportsbook', kind: 'sportsbook', game: 'sportsbook', hidePadlockWhenGlobal: true },
  { label: 'Live Casino', kind: 'casino', game: 'casino', hidePadlockWhenGlobal: true },
  { label: 'Card Game', kind: 'card', game: 'cardGame', hidePadlockWhenGlobal: true, blockedBySportsbook: true },
  { label: 'Live Game', kind: 'liveGame', game: 'liveGame', blockedBySportsbook: true },
  { label: 'Premium-Sportsbook', kind: 'premiumSportsbook', game: 'premiumSportsbook', blockedBySportsbook: true },
  { label: 'Horse Racing', kind: 'horseRacing', game: 'horseRacing', blockedBySportsbook: true },
  { label: 'Google Authenticator', game: 'googleAuthenticator' },
  { label: 'Transfer Lock', kind: 'transfer' },
]

const CRICKET: ProductRow[] = [
  { label: 'Cricket', game: 'cricket', blockedBySportsbook: true },
  { label: 'Line Session', kind: 'lineSession', game: 'lineSession', blockedBySportsbook: true },
  { label: 'Tied Match', kind: 'tiedMatch', game: 'tiedMatch', blockedBySportsbook: true },
  { label: 'UOF Markets', game: 'uofMarkets', blockedBySportsbook: true },
  { label: 'Match odds', kind: 'matchOdds', game: 'matchOdds', blockedBySportsbook: true },
  { label: 'Bookmaker', kind: 'bookmaker', game: 'bookmaker', blockedBySportsbook: true },
  { label: 'Completed Match', kind: 'completedMatch', game: 'completedMatch', blockedBySportsbook: true },
  { label: 'Custom Session', kind: 'customSession', game: 'customSession', blockedBySportsbook: true },
]

const SPORTS: ProductRow[] = [
  { label: 'Esports', game: 'esports', blockedBySportsbook: true },
  { label: 'Greyhound Racing', game: 'greyhoundRacing', blockedBySportsbook: true },
  { label: 'Darts', game: 'darts', blockedBySportsbook: true },
  { label: 'Election', game: 'election', blockedBySportsbook: true },
  { label: 'Tennis', game: 'tennis', blockedBySportsbook: true },
  { label: 'Soccer', game: 'soccer', blockedBySportsbook: true },
  { label: 'Soccer Super', game: 'soccerSuper', blockedBySportsbook: true },
  { label: 'Horse Racing', game: 'sportHorseRacing', blockedBySportsbook: true },
  { label: 'Volleyball', game: 'volleyball', blockedBySportsbook: true },
  { label: 'Snooker', game: 'snooker', blockedBySportsbook: true },
  { label: 'Rugby League', game: 'rugbyLeague', blockedBySportsbook: true },
  { label: 'Artemis', game: 'artemis', blockedBySportsbook: true },
  { label: 'Rugby Union', game: 'rugbyUnion', blockedBySportsbook: true },
]

function flagOn(node: HierarchyNode, game: GameStatusGame): boolean {
  const g = node.games
  if (!g) return true
  const v = g[game]
  return v !== false
}

function padlockOn(node: HierarchyNode, kind: GameLockKind): boolean {
  return node.gameLocks?.[kind]?.status === true
}

function LockToggle({
  locked,
  disabled,
  label,
  onClick,
}: {
  locked: boolean
  disabled?: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`cm-events-lock${locked ? ' is-locked' : ' is-unlocked'}${disabled ? ' is-muted' : ''}`}
      disabled={disabled}
      aria-label={
        disabled
          ? `${label} lock unavailable`
          : locked
            ? `Unlock ${label}`
            : `Lock ${label}`
      }
      onClick={onClick}
    >
      <Icon name={locked ? 'lock' : 'unlock'} size={16} />
    </button>
  )
}

function GameRow({
  row,
  node,
  bookLocked,
  sportsbookLocked,
  closed,
  busy,
  readOnly,
  onLock,
  onStatus,
}: {
  row: ProductRow
  node: HierarchyNode
  bookLocked: boolean
  sportsbookLocked: boolean
  closed: boolean
  busy: boolean
  readOnly: boolean
  onLock: (kind: GameLockKind) => void
  onStatus: (game: GameStatusGame, enabled: boolean) => void
}) {
  const locked = row.kind ? padlockOn(node, row.kind) : false
  const enabled = row.game ? flagOn(node, row.game) : true
  const hidePadlock = Boolean(row.hidePadlockWhenGlobal && bookLocked)
  const padlockDisabled =
    readOnly ||
    closed ||
    busy ||
    (row.blockedBySportsbook && sportsbookLocked && !locked)
  return (
    <div className="cm-game-lock-row">
      <span className="cm-game-lock-label">{row.label}</span>
      {row.kind && !hidePadlock ? (
        <LockToggle
          locked={locked}
          disabled={padlockDisabled}
          label={row.label}
          onClick={() => onLock(row.kind!)}
        />
      ) : hidePadlock ? (
        <span className="cm-hint">Global lock</span>
      ) : (
        <span className="cm-game-lock-pad-spacer" />
      )}
      {row.game ? (
        <label className="cm-game-lock-switch">
          <input
            type="checkbox"
            checked={enabled}
            disabled={readOnly || closed || busy}
            aria-label={`${row.label} ${enabled ? 'on' : 'off'}`}
            onChange={(event) => onStatus(row.game!, event.target.checked)}
          />
          <span>{enabled ? 'On' : 'Off'}</span>
        </label>
      ) : null}
    </div>
  )
}

/**
 * Lab lock-modal — every product padlock, On/Off, Sportswise, Authenticator, Transfer.
 */
export function GameLockModal({
  node,
  bookLocked,
  readOnly = false,
  onClose,
}: {
  node: HierarchyNode
  bookLocked: boolean
  /** View lock state without padlock / On-Off mutations. */
  readOnly?: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const me = useCmMe()
  // Live grant check: staff may lose Member/Event lock mid-session.
  const writeAllowed = !readOnly && canGameLock(me.data?.caller)

  const assertGameLock = async (): Promise<boolean> => {
    if (readOnly) return false
    const fresh = await me.refetch()
    if (canGameLock(fresh.data?.caller)) return true
    toast.error('Missing access grant: Member Lock/Unlock')
    return false
  }

  const qc = useQueryClient()
  const closed = hierarchyLockUiBlocked(node.status)
  const [locks, setLocks] = useState(node.gameLocks)
  const [games, setGames] = useState(node.games)
  useEffect(() => {
    setLocks(node.gameLocks)
    setGames(node.games)
  }, [node])
  const view: HierarchyNode = { ...node, gameLocks: locks, games }

  const sportsbookLocked = padlockOn(view, 'sportsbook')
  const [confirmKind, setConfirmKind] = useState<GameLockKind | null>(null)

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['cm', 'hierarchy'] })
  }

  const lockMut = useMutation({
    mutationFn: (kind: GameLockKind) => cmApi.hierarchyGameLock(authHeaders(), node.id, kind),
    onSuccess: (res) => {
      toast.success(res.message)
      setConfirmKind(null)
      setLocks(res.gameLocks)
      invalidate()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Game lock failed')
    },
  })

  const statusMut = useMutation({
    mutationFn: (opts: { game: GameStatusGame; enabled: boolean }) =>
      cmApi.hierarchyGameStatus(authHeaders(), node.id, opts.game, opts.enabled),
    onSuccess: (res) => {
      toast.success(res.message)
      setGames((cur) => ({ ...cur, [res.game]: res.enabled }))
      invalidate()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Game status failed')
    },
  })

  const busy = lockMut.isPending || statusMut.isPending

  const rowProps = {
    node: view,
    bookLocked,
    sportsbookLocked,
    closed,
    busy,
    readOnly: !writeAllowed,
    onLock: (kind: GameLockKind) => {
      if (!writeAllowed) return
      setConfirmKind(kind)
    },
    onStatus: (game: GameStatusGame, enabled: boolean) => {
      void assertGameLock().then((ok) => {
        if (ok) statusMut.mutate({ game, enabled })
      })
    },
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Lock"
      subtitle={
        writeAllowed
          ? (node.username ?? node.name)
          : `${node.username ?? node.name} · view only`
      }
      size="lg"
      footer={
        confirmKind && writeAllowed ? (
          <div className="cm-game-lock-confirm" role="dialog" aria-label="Confirm game lock">
            <p>
              Lock or unlock {confirmKind} for {node.username ?? node.name}?
            </p>
            <div className="cm-dl-actions">
              <Button type="button" variant="secondary" onClick={() => setConfirmKind(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={busy}
                onClick={() => {
                  void assertGameLock().then((ok) => {
                    if (ok && confirmKind) lockMut.mutate(confirmKind)
                  })
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div className="cm-game-lock-grid cm-game-lock-products">
        {PRODUCTS.map((row) => (
          <GameRow key={`${row.label}-${row.kind ?? row.game}`} row={row} {...rowProps} />
        ))}
      </div>
      <h3 className="cm-game-lock-section">Sportswise</h3>
      {sportsbookLocked ? (
        <p className="cm-hint">Sportsbook locked</p>
      ) : null}
      <div className="cm-game-lock-sports">
        <div className="cm-game-lock-cricket">
          {CRICKET.map((row) => (
            <GameRow key={row.label} row={row} {...rowProps} />
          ))}
        </div>
        <div className="cm-game-lock-grid">
          {SPORTS.map((row) => (
            <GameRow key={row.game} row={row} {...rowProps} />
          ))}
        </div>
      </div>
    </Modal>
  )
}
